import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Text,
  Button,
  Checkbox,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Toolbar,
  ToolbarButton,
  Tooltip,
  Spinner,
} from "@fluentui/react-components";
import {
  Warning24Regular,
  ArrowLeft20Regular,
  Checkmark24Regular,
  ErrorCircle24Regular,
} from "@fluentui/react-icons";
import { useDevice } from "../../../model";
import { useAppStyles } from "../../styles/appStyles";
import { config } from "../../../config/environment";

interface UnregisterPageProps {
  onBack: () => void;
  onSuccess?: () => void;
}

const UnregisterPage: React.FC<UnregisterPageProps> = ({ onBack, onSuccess }) => {
  const { t } = useTranslation();
  const styles = useAppStyles();
  const { fingerprint, unregisterDevice } = useDevice();

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isUnregistering, setIsUnregistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleUnregister = async () => {
    if (!isConfirmed) {
      setError(t("unregister.error.confirm_required", "Musisz potwierdzić operację"));
      return;
    }

    setIsUnregistering(true);
    setError(null);

    try {
      console.log("🚀 Starting unregister process...");

      const unregisterUrl = `${config.api.baseUrl}/api/v1/Subscription/unregister`;
      console.log("📡 Calling unregister API:", unregisterUrl);

      const response = await fetch(unregisterUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          deviceFingerprint: fingerprint,
        }),
      });

      if (!response.ok) {
        let errorMessage = t("unregister.error.unknown", "Nieznany błąd odinstalowania");

        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            console.error("❌ API Error response:", errorData);

            if (errorData.details) {
              errorMessage = errorData.details;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } else {
            errorMessage = `${t("unregister.error.http", "Błąd HTTP")} ${response.status}`;
          }
        } catch (parseError) {
          console.error("❌ Error parsing error response:", parseError);
          errorMessage = `${t("unregister.error.http", "Błąd HTTP")} ${response.status}`;
        }

        throw new Error(errorMessage);
      }

      console.log("✅ Unregister API successful");

      // Call unregisterDevice from DeviceContext to clear localStorage and reset state
      console.log("🧹 Clearing local data and resetting to TRIAL...");
      unregisterDevice();

      setSuccess(true);

      // Po krótkiej chwili wróć lub wywołaj callback sukcesu
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        } else {
          onBack();
        }
      }, 2000);
    } catch (err) {
      console.error("❌ Unregister error:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsUnregistering(false);
    }
  };

  return (
    <div className={styles.appPageContainer}>
      <div className={styles.appPageHeader} style={{ background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)" }}>
        <Warning24Regular className={styles.appPageHeaderIcon} style={{ color: "#ffffff" }} />
        <Text className={styles.appPageTitle} style={{ color: "#ffffff" }}>
          {t("unregister.title", "Odinstalowanie klucza aktywacyjnego")}
        </Text>
      </div>

      <Toolbar className={styles.toolbar}>
        <Tooltip
          content={t("unregister.toolbar.back_tooltip", "Powrót do poprzedniej strony")}
          relationship="description"
        >
          <ToolbarButton
            className={styles.toolbarButton}
            aria-label={t("unregister.toolbar.back", "Powrót")}
            icon={<ArrowLeft20Regular className={styles.backIcon} />}
            onClick={onBack}
            disabled={isUnregistering}
          >
            {t("unregister.toolbar.back", "Powrót")}
          </ToolbarButton>
        </Tooltip>
      </Toolbar>

      <div className={styles.appPageContent}>
        {/* Success Message */}
        {success && (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>
                {t("unregister.success.title", "Odinstalowanie pomyślne!")}
              </MessageBarTitle>
              {t(
                "unregister.success.message",
                "Klucz został odinstalowany. Aplikacja przeszła w tryb TRIAL. Przekierowywanie..."
              )}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Error Message */}
        {error && !success && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {t("unregister.error.title", "Błąd odinstalowania")}
              </MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Warnings and Confirmations */}
        {!success && (
          <>
            {/* Critical Warning */}
            <MessageBar intent="error" style={{ marginBottom: "16px" }}>
              <MessageBarBody>
                <MessageBarTitle>
                  ⚠️ {t("unregister.warning.critical", "UWAGA: Ta operacja spowoduje usunięcie wszystkich danych!")}
                </MessageBarTitle>
              </MessageBarBody>
            </MessageBar>

            {/* What will be deleted */}
            <MessageBar intent="warning" style={{ marginBottom: "16px" }}>
              <MessageBarBody>
                <MessageBarTitle>
                  {t("unregister.warning.what_deleted", "Co zostanie usunięte?")}
                </MessageBarTitle>
                <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
                  <li>{t("unregister.warning.item1", "Powiązanie tej instancji aplikacji z Twoją subskrypcją")}</li>
                  <li>
                    {t(
                      "unregister.warning.item2",
                      "Wszystkie dodane firmy i ich dane dostępowe (Client ID i Client Secret)"
                    )}
                  </li>
                  <li>
                    {t("unregister.warning.item3", "Lista przypisanych EUP (Miejsc Prowadzenia Działalności)")}
                  </li>
                  <li>{t("unregister.warning.item4", "Wszystkie lokalne ustawienia i konfiguracje")}</li>
                </ul>
              </MessageBarBody>
            </MessageBar>

            {/* Info about Trial mode */}
            <MessageBar intent="info" style={{ marginBottom: "16px" }}>
              <MessageBarBody>
                <MessageBarTitle>
                  {t("unregister.info.after_unregister", "Po odinstalowaniu klucza:")}
                </MessageBarTitle>
                {t(
                  "unregister.info.trial_mode",
                  "Aplikacja przejdzie w tryb TRIAL (próbny). Aby ponownie korzystać z pełnej wersji, będziesz musiał/a wprowadzić nowy klucz aktywacyjny."
                )}
              </MessageBarBody>
            </MessageBar>

            {/* Confirmation Checkbox */}
            <div style={{ marginBottom: "24px", padding: "16px", background: "#f3f2f1", borderRadius: "8px" }}>
              <Checkbox
                checked={isConfirmed}
                onChange={(e, data) => setIsConfirmed(data.checked === true)}
                disabled={isUnregistering}
                label={t(
                  "unregister.confirmation.checkbox",
                  "Potwierdzam, że rozumiem konsekwencje tej operacji i chcę odinstalować klucz aktywacyjny z tej instancji aplikacji."
                )}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Button
                appearance="primary"
                onClick={handleUnregister}
                disabled={isUnregistering || !isConfirmed}
                icon={isUnregistering ? <Spinner size="tiny" /> : undefined}
                style={{
                  width: "100%",
                  background: isConfirmed && !isUnregistering ? "#d32f2f" : undefined,
                  borderColor: isConfirmed && !isUnregistering ? "#d32f2f" : undefined,
                }}
              >
                {isUnregistering
                  ? t("unregister.button.unregistering", "Odinstalowywanie...")
                  : t("unregister.button.unregister", "Odinstaluj klucz")}
              </Button>
              <Button
                appearance="secondary"
                onClick={onBack}
                disabled={isUnregistering}
                style={{ width: "100%" }}
              >
                {t("unregister.button.cancel", "Anuluj")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UnregisterPage;
